import { useEffect, useRef } from "react";
import { useAppSelector } from "../../app";
import useMultipleGroupBlocks from "../../configMap/hooks/useMultipleGroupBlocks";
import {
  selectIsShowingSnapshot,
  selectSnapshotView,
} from "../../annotation/selectors/annotationReducerSelector";

/**
 * Rows of a multiple group block are drawn from a count calculated once at document load,
 * from the current annotations. A snapshot can hold more rows than the current version
 * (a line was removed while editing), in which case those rows would not be drawn at all,
 * or fewer, leaving empty rows behind. Recalculating on each switch keeps the row count
 * with the annotations actually on screen.
 *
 * Only the switch itself triggers a recalculation: doing it on every annotation change
 * would drop empty rows the user just added while editing.
 */
const useAnnotationViewGroupBlockCounts = () => {
  const { setInitialMultipleGroupBlocksCount } = useMultipleGroupBlocks();

  const isShowingSnapshot = useAppSelector(selectIsShowingSnapshot);
  const snapshotView = useAppSelector(selectSnapshotView);
  const configMap = useAppSelector((state) => state.configMapReducer.configMap);
  const tableControlAnnotations = useAppSelector(
    (state) => state.annotationReducer.tableControlAnnotations,
  );
  const sideControlAnnotations = useAppSelector(
    (state) => state.annotationReducer.sideControlAnnotations,
  );
  const categorizationAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState.categorizationAnnotations,
  );

  const snapshotIsReady = isShowingSnapshot && snapshotView != null;

  const latest = useRef({
    configMap,
    snapshotView,
    tableControlAnnotations,
    sideControlAnnotations,
    categorizationAnnotations,
  });

  useEffect(() => {
    latest.current = {
      configMap,
      snapshotView,
      tableControlAnnotations,
      sideControlAnnotations,
      categorizationAnnotations,
    };
  });

  useEffect(() => {
    const current = latest.current;
    if (!current.configMap) {
      return;
    }

    if (isShowingSnapshot && !current.snapshotView) {
      return;
    }

    const annotations =
      current.snapshotView && isShowingSnapshot
        ? current.snapshotView.annotations
        : [
            ...current.tableControlAnnotations,
            ...current.sideControlAnnotations,
          ];

    setInitialMultipleGroupBlocksCount(
      current.configMap,
      annotations,
      current.categorizationAnnotations || [],
    );
  }, [isShowingSnapshot, snapshotIsReady, setInitialMultipleGroupBlocksCount]);
};

export default useAnnotationViewGroupBlockCounts;
